// VisitorDashboard.js - Mit mobil-freundlicher stündlicher Ansicht
import React, { useState, useEffect } from "react";
import {
  getDatabase,
  ref,
  onValue,
  query,
  orderByChild,
  startAt,
  endAt,
} from "firebase/database";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { getAuth, signInAnonymously } from "firebase/auth";

const VisitorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visitorData, setVisitorData] = useState([]);
  const [stats, setStats] = useState({ today: 0, week: 0, month: 0 });
  const [showChart, setShowChart] = useState(false);
  const [timeRange, setTimeRange] = useState("week"); // 'day', 'week', 'month', 'year', 'singleDay'
  const [isHourlyView, setIsHourlyView] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  // Überprüfen, ob das Gerät mobil ist
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const auth = getAuth();
        await signInAnonymously(auth);

        const db = getDatabase();
        const visitsRef = ref(db, "visits");

        // Immer die letzten 30 Tage abrufen für die Stats
        const now = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(now.getDate() - 30);

        // Startzeitpunkt für die Query (30 Tage zurück)
        const startTimestamp = thirtyDaysAgo.getTime();

        const timeQuery = query(
          visitsRef,
          orderByChild("startTime"),
          startAt(startTimestamp)
        );

        const unsubscribe = onValue(
          timeQuery,
          (snapshot) => {
            const visits = snapshot.val() || {};
            const visitArray = Object.values(visits);

            // --- Stats Berechnung ---
            let todayCount = 0;
            let weekCount = 0;
            let monthCount = 0;

            const todayStr = formatDate(new Date());
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(now.getDate() - 7);
            // Reset hours to compare dates properly
            oneWeekAgo.setHours(0, 0, 0, 0);

            // Für Chart-Daten (gefiltert nach timeRange)
            let chartStartDate = new Date();
            switch (timeRange) {
              case "day": chartStartDate.setDate(now.getDate() - 1); break;
              case "week": chartStartDate.setDate(now.getDate() - 7); break;
              case "month": chartStartDate.setMonth(now.getMonth() - 1); break;
              case "year": chartStartDate.setFullYear(now.getFullYear() - 1); break;
              case "singleDay":
                if (selectedDate) {
                  chartStartDate = new Date(selectedDate);
                }
                break;
              default: chartStartDate.setDate(now.getDate() - 7);
            }
            // Reset hours
            chartStartDate.setHours(0, 0, 0, 0);


            visitArray.forEach((visit) => {
              if (!visit.startTime) return;

              const visitDate = new Date(visit.startTime);
              const visitDateStr = formatDate(visitDate);

              // Today
              if (visitDateStr === todayStr) {
                todayCount++;
              }
              // Last 7 Days
              if (visitDate >= oneWeekAgo) {
                weekCount++;
              }
              // Last 30 Days (alle geladenen)
              monthCount++;
            });

            setStats({
              today: todayCount,
              week: weekCount,
              month: monthCount
            });


            // --- Chart Datenaufbereitung ---

            if (isHourlyView) {
              // Stündliche Besuche für den einzelnen Tag
              const hourlyVisitMap = {};
              for (let hour = 0; hour < 24; hour++) { hourlyVisitMap[hour] = 0; }

              visitArray.forEach((visit) => {
                if (visit.startTime) {
                  const visitDate = new Date(visit.startTime);
                  // Filter: Nur Datum von selectedDate
                  if (formatDate(visitDate) === selectedDate) {
                    const hour = visitDate.getHours();
                    hourlyVisitMap[hour]++;
                  }
                }
              });
              const formattedData = Object.keys(hourlyVisitMap).map((hour) => ({
                hour: `${hour}:00`,
                visits: hourlyVisitMap[hour],
              }));
              setVisitorData(formattedData);

            } else {
              // Tägliche Besuche
              const dailyVisitMap = {};
              let currentDate = new Date(chartStartDate);
              const iterEndDate = timeRange === 'singleDay' ? new Date(selectedDate) : new Date();

              // Map initialisieren
              while (currentDate <= iterEndDate) {
                const dateKey = formatDate(currentDate);
                dailyVisitMap[dateKey] = 0;
                currentDate.setDate(currentDate.getDate() + 1);
              }

              visitArray.forEach((visit) => {
                if (visit.startTime) {
                  const visitDate = new Date(visit.startTime);
                  const dateKey = formatDate(visitDate);
                  // Ensure comparison ignores time for daily buckets
                  const vDateStart = new Date(visitDate);
                  vDateStart.setHours(0, 0, 0, 0);

                  if (vDateStart >= chartStartDate && dailyVisitMap[dateKey] !== undefined) {
                    dailyVisitMap[dateKey]++;
                  }
                }
              });

              const formattedData = Object.keys(dailyVisitMap).sort().map((date) => ({
                date: formatDisplayDate(date),
                fullDate: date,
                visits: dailyVisitMap[date],
              }));
              setVisitorData(formattedData);
            }

            setLoading(false);
          },
          (error) => {
            console.error("Firebase Read Error:", error);
            setError(error.message);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        console.error("Auth Error:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, selectedDate, isHourlyView]);

  // Hilfsfunktion für Datumsformatierung (YYYY-MM-DD)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Hilfsfunktion für Datumsanzeige (DD.MM.)
  const formatDisplayDate = (dateString) => {
    const parts = dateString.split("-");
    return `${parts[2]}.${parts[1]}.`;
  };

  // Benutzerdefinierte Tooltip-Komponente
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          {isHourlyView ? (
            <p
              style={{ margin: "0" }}
            >{`${label}: ${payload[0].value} Besuche`}</p>
          ) : (
            <>
              <p style={{ margin: "0 0 5px 0" }}>
                <strong>{item.fullDate}</strong>
              </p>
              <p style={{ margin: "0" }}>{`${payload[0].value} Besuche`}</p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  // Handler für den Klick auf einen Balken (um einen einzelnen Tag anzuzeigen)
  const handleBarClick = (data) => {
    if (!isHourlyView && data && data.fullDate) {
      setSelectedDate(data.fullDate);
      setTimeRange("singleDay");
      setIsHourlyView(true);
    }
  };

  // Handler für den Zurück-Button in der Stundenansicht
  const handleBackToDateView = () => {
    setIsHourlyView(false);
    setTimeRange("week");
    setSelectedDate("");
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', height: '300px', alignItems: 'center' }}>
        <p>Lade Besucherstatistiken...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" style={{ display: 'flex', justifyContent: 'center', height: '300px', alignItems: 'center', color: 'red' }}>
        <p>Fehler beim Laden: {error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Heute</div>
          <div className="kpi-value">{stats.today}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Letzten 7 Tage</div>
          <div className="kpi-value">{stats.week}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Letzten 30 Tage</div>
          <div className="kpi-value">{stats.month}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <button
          className="toggle-chart-btn"
          onClick={() => setShowChart(!showChart)}
        >
          {showChart ? "Diagramm ausblenden" : "Diagramm anzeigen"}
        </button>
      </div>

      {/* Chart Section (Conditional) */}
      {showChart && (
        <div className="chart-container">

          <div className="chart-header">
            {isHourlyView ? (
              <button
                onClick={handleBackToDateView}
                className="back-btn"
                style={{ cursor: "pointer" }}
              >
                ← Zurück zur Tagesansicht
              </button>
            ) : (
              <p>* Tippe auf einen Balken für Details</p>
            )}

            {!isHourlyView && (
              <select
                className="time-select"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option value="day">Letzte 24 Stunden</option>
                <option value="week">Letzte Woche</option>
                <option value="month">Letzter Monat</option>
              </select>
            )}
          </div>

          <div className={`chart-wrapper ${isHourlyView && isMobile ? 'mobile-tall' : ''}`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={visitorData}
                margin={{
                  top: 20,
                  right: 30,
                  left: isHourlyView && isMobile ? 0 : 20,
                  bottom: isHourlyView && isMobile ? 10 : 60,
                }}
                onClick={(data) =>
                  !isHourlyView &&
                  data.activePayload &&
                  handleBarClick(data.activePayload[0].payload)
                }
                layout={isHourlyView && isMobile ? "vertical" : "horizontal"}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                {isHourlyView && isMobile ? (
                  <>
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="hour"
                      type="category"
                      width={40}
                      tick={{ fontSize: 12 }}
                    />
                  </>
                ) : (
                  <>
                    <XAxis
                      dataKey={isHourlyView ? "hour" : "date"}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      dy={10}
                    />
                    <YAxis
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                    />
                  </>
                )}
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar
                  dataKey="visits"
                  name="Besuche"
                  fill="#003366"
                  radius={[4, 4, 0, 0]}
                  barSize={isHourlyView ? 20 : 40}
                  cursor={!isHourlyView ? "pointer" : "default"}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorDashboard;
